import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const ProgressSummary = ({ resolutions, progress }) => {
	const { t } = useLanguage();

	if (!resolutions || resolutions.length === 0) {
		return null;
	}

	const getProgressColor = (progressValue) => {
		if (progressValue < 50) return 'bg-red-500';
		if (progressValue < 90) return 'bg-blue-500';
		return 'bg-green-500';
	};

	const getProgressTextColor = (progressValue) => {
		if (progressValue < 50) return 'text-red-600';
		if (progressValue < 90) return 'text-blue-600';
		return 'text-green-600';
	};

	const getBgColor = (progressValue) => {
		if (progressValue < 50) return 'bg-red-50 border-red-200';
		if (progressValue < 90) return 'bg-blue-50 border-blue-200';
		return 'bg-green-50 border-green-200';
	};

	const safeProgress = !progress ? 0 : progress > 100 ? 100 : progress;

	// Calculate some stats
	const totalResolutions = resolutions.length;
	const completedResolutions = resolutions.filter((r) => r.statusCode === 'completed').length;
	const aheadResolutions = resolutions.filter((r) => r.statusCode === 'ahead').length;
	const behindResolutions = resolutions.filter((r) => r.statusCode === 'behind').length;
	const onTrackResolutions = resolutions.filter((r) => r.statusCode === 'on-track').length;
	const failedResolutions = resolutions.filter((r) => r.statusCode === 'failed').length;

	return (
		<div className={`mb-6 p-4 rounded-lg border ${getBgColor(safeProgress)}`}>
			<div className='flex items-center justify-between mb-3'>
				<h3 className='text-lg font-semibold text-gray-900'>{t('overallProgress')}</h3>
				<span className={`text-2xl font-bold ${getProgressTextColor(safeProgress)}`}>
					{safeProgress.toFixed(1)}%
				</span>
			</div>

			<div className='w-full bg-gray-200 rounded-full h-3 mb-3'>
				<div
					className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(safeProgress)}`}
					style={{ width: `${safeProgress}%` }}
				></div>
			</div>

			{/* Stats summary */}
			<div className='flex items-center justify-between text-sm text-gray-600'>
				<div className='flex items-center space-x-4'>
					<span>
						{totalResolutions} {t('total')}
					</span>
					{completedResolutions > 0 && (
						<span className='text-green-600'>
							✅ {completedResolutions} {t('completed')}
						</span>
					)}
					{aheadResolutions > 0 && (
						<span className='text-blue-600'>
							🚀 {aheadResolutions} {t('ahead')}
						</span>
					)}
					{behindResolutions > 0 && (
						<span className='text-red-600'>
							⚠️ {behindResolutions} {t('behind')}
						</span>
					)}
					{onTrackResolutions > 0 && (
						<span className='text-gray-600'>
							📈 {onTrackResolutions} {t('on-track')}
						</span>
					)}
					{failedResolutions > 0 && (
						<span className='text-red-800'>
							❌ {failedResolutions} {t('failed')}
						</span>
					)}
				</div>
			</div>
		</div>
	);
};

export default ProgressSummary;
